import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/solid'
import { clsx } from 'clsx'

interface LogEntry {
    level?: number
    time?: number
    msg?: string
    tag?: string
    // Common fields
    status?: string
    error?: string
    warnings?: string[]
    // Navigation
    targetUrl?: string
    currentUrl?: string
    initialUrl?: string
    // Cookies
    count?: number
    // Site-specific
    site?: string
    result?: string
    reason?: string
    // Raw fallback
    raw?: string
}

// Map tags to human-readable step names
const STEP_LABELS: Record<string, string> = {
    // Main workflow
    snapshot: 'Starting screenshot',
    cookies: 'Applying cookies',
    validation: 'Validating input',
    proxy: 'Configuring proxy',
    nav: 'Navigating to page',
    nav_unexpected: 'Unexpected navigation',
    page_load: 'Processing page',
    auth: 'Authentication check',
    mode: 'Preparing output',
    done: 'Complete',
    fail_screenshot: 'Capturing failure',
    pause: 'Paused for inspection',
    // Instagram
    ig_cookie_prompt: 'Dismissing cookie dialog',
    ig_page_state: 'Checking page state',
    ig_login: 'Login page detected',
    ig_comments: 'Loading comments',
    ig_comment_text: 'Finding comment',
    ig_scroll: 'Scrolling to comment',
    ig_scroll_verify: 'Verifying position',
    // Facebook
    fb_cookie_prompt: 'Dismissing cookie dialog',
    fb_unsupported: 'Unsupported page type',
    fb_content_unavailable: 'Content unavailable',
    fb_comment_text: 'Finding comment',
    fb_comments_wait: 'Loading comments'
}

// Pino log levels: 10=trace, 20=debug, 30=info, 40=warn, 50=error, 60=fatal
const LEVEL_WARN = 40
const LEVEL_ERROR = 50

function parseLogLines(logs: string): LogEntry[] {
    return logs
        .split('\n')
        .filter((line) => !line.startsWith('START') && !line.startsWith('END') && line.trim())
        .map((line) => {
            try {
                return JSON.parse(line) as LogEntry
            } catch {
                return { raw: line }
            }
        })
}

function getStepIcon(entry: LogEntry) {
    const isError = (entry.level ?? 0) >= LEVEL_ERROR
    const isWarn = (entry.level ?? 0) >= LEVEL_WARN && (entry.level ?? 0) < LEVEL_ERROR

    if (isError) {
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
    }
    if (isWarn) {
        return <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
    }
    // All completed steps (not errors/warnings) are green
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />
}

function formatStepDetails(entry: LogEntry): string | null {
    const parts: string[] = []

    if (entry.msg && entry.msg !== STEP_LABELS[entry.tag ?? '']) {
        parts.push(entry.msg)
    }

    if (entry.error) {
        parts.push(entry.error)
    }

    if (entry.warnings?.length) {
        parts.push(`Warnings: ${entry.warnings.join(', ')}`)
    }

    if (entry.count !== undefined) {
        parts.push(`${entry.count} cookies`)
    }

    if (entry.site) {
        parts.push(`Platform: ${entry.site}`)
    }

    if (entry.result) {
        parts.push(`Result: ${entry.result}`)
    }

    if (entry.reason) {
        parts.push(`Reason: ${entry.reason}`)
    }

    if (entry.currentUrl && entry.initialUrl && entry.currentUrl !== entry.initialUrl) {
        parts.push(`Redirected to: ${entry.currentUrl}`)
    }

    return parts.length > 0 ? parts.join(' - ') : null
}

function formatTime(timestamp?: number): string | null {
    if (!timestamp) return null
    try {
        return new Date(timestamp).toLocaleTimeString()
    } catch {
        return null
    }
}

type Props = {
    logs: string
}

export default function ScreenshotStepsTimeline({ logs }: Props) {
    const entries = parseLogLines(logs)

    // Filter to entries with tags we care about
    const taggedEntries = entries.filter((e) => e.tag && STEP_LABELS[e.tag])

    // Find if there's an error
    const hasError = entries.some((e) => (e.level ?? 0) >= LEVEL_ERROR)
    const errorEntry = entries.find((e) => (e.level ?? 0) >= LEVEL_ERROR)

    // Group by tag to avoid duplicates, keeping the most recent/important
    const seenTags = new Set<string>()
    const deduped = taggedEntries.filter((entry) => {
        if (!entry.tag) return false
        // For errors/warnings, always show
        if ((entry.level ?? 0) >= LEVEL_WARN) return true
        // For other entries, show first occurrence of each tag
        if (seenTags.has(entry.tag)) return false
        seenTags.add(entry.tag)
        return true
    })

    if (deduped.length === 0) {
        return (
            <div className="text-gray-500 text-sm italic">
                No structured step data available in logs.
            </div>
        )
    }

    return (
        <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-2.5 top-3 bottom-3 w-0.5 bg-gray-200" />

            <div className="space-y-3">
                {deduped.map((entry, index) => {
                    const isError = (entry.level ?? 0) >= LEVEL_ERROR
                    const isWarn = (entry.level ?? 0) >= LEVEL_WARN && !isError
                    const label = STEP_LABELS[entry.tag ?? ''] || entry.tag || 'Unknown step'
                    const details = formatStepDetails(entry)
                    const time = formatTime(entry.time)

                    return (
                        <div key={index} className="relative flex items-start gap-3 pl-8">
                            {/* Icon on timeline */}
                            <div className="absolute left-0 bg-white">
                                {getStepIcon(entry)}
                            </div>

                            {/* Content */}
                            <div
                                className={clsx(
                                    'flex-1 rounded-md px-3 py-2 text-sm',
                                    isError && 'bg-red-50 border border-red-200',
                                    isWarn && 'bg-amber-50 border border-amber-200',
                                    !isError && !isWarn && 'bg-gray-50'
                                )}
                            >
                                <div className="flex items-center justify-between">
                                    <span
                                        className={clsx(
                                            'font-medium',
                                            isError && 'text-red-700',
                                            isWarn && 'text-amber-700',
                                            !isError && !isWarn && 'text-gray-700'
                                        )}
                                    >
                                        {label}
                                    </span>
                                    {time && (
                                        <span className="text-xs text-gray-400">{time}</span>
                                    )}
                                </div>
                                {details && (
                                    <div
                                        className={clsx(
                                            'text-xs mt-1',
                                            isError && 'text-red-600',
                                            isWarn && 'text-amber-600',
                                            !isError && !isWarn && 'text-gray-500'
                                        )}
                                    >
                                        {details}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Summary */}
            <div className="mt-4 pt-3 border-t border-gray-200">
                <div
                    className={clsx(
                        'text-sm font-medium',
                        hasError ? 'text-red-600' : 'text-green-600'
                    )}
                >
                    {hasError ? (
                        <>
                            <ExclamationCircleIcon className="h-4 w-4 inline mr-1" />
                            Screenshot failed
                            {errorEntry?.msg && `: ${errorEntry.msg}`}
                        </>
                    ) : (
                        <>
                            <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                            Screenshot completed successfully
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
